import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from 'app/@core/auth/auth.service';
import { TenantContextService } from 'app/@core/services/utility/tenant-context.service';

/**
 * Global Angular ErrorHandler that intercepts Firebase "permission-denied" errors
 * and treats them as a tenant-suspension signal when the user is authenticated.
 *
 * Why this approach:
 *   - Firestore security rules enforce `isCallerTenantActive()` on every collection.
 *   - When a tenant's status is set to 'suspended', all Firestore reads/writes from
 *     that tenant begin throwing `FirebaseError { code: 'permission-denied' }`.
 *   - This handler catches those errors globally (both from async operations and
 *     from Observable streams subscribed via AsyncPipe) and immediately redirects
 *     the user to the /suspended landing page.
 *   - The user is signed out on arrival at /suspended, clearing all in-memory state.
 *
 * Super-admin exclusion:
 *   - super_admin users have `tenantId === 'system'`.
 *   - The only permission-denied a super_admin can legitimately encounter is
 *     an attempt to access the PHI `clients` collection, which is a code bug,
 *     not a suspension event. We let the default handler log it.
 *
 * Injector pattern:
 *   ErrorHandler is instantiated very early in the Angular bootstrap process,
 *   before many DI tokens are ready. We use Injector to lazily resolve Router,
 *   AuthService, and TenantContextService only when handleError is called.
 */
@Injectable()
export class FirebasePermissionErrorHandler implements ErrorHandler {

    // Track whether a redirect is already in flight to avoid duplicate navigations.
    private _redirecting = false;

    constructor(private readonly _injector: Injector) {}

    handleError(error: any): void {
        if (this._isPermissionDenied(error) && !this._redirecting) {
            const tenantCtx = this._injector.get(TenantContextService);

            // Only intercept for tenant-scoped staff roles.
            // super_admin (tenantId === 'system') hitting permission-denied is a
            // code bug — fall through to default logging.
            const tenantId = tenantCtx.currentTenantId;
            const role = tenantCtx.currentRole;

            if (tenantId && role !== 'super_admin') {
                this._redirecting = true;

                const authService = this._injector.get(AuthService);
                const router = this._injector.get(Router);

                // Navigate immediately so the user sees the suspended page
                // without delay. Sign-out completes in the background.
                router.navigate(['/suspended']).then(() => {
                    // Reset guard after navigation completes so future
                    // sign-ins can trigger this handler again if needed.
                    this._redirecting = false;
                });

                // Sign out in background — /suspended handles state cleanup.
                authService.signOut();
                return;
            }
        }

        // Default: log to console so non-suspension errors are not silently swallowed.
        console.error('[GlobalErrorHandler]', error);
        console.log(error);
    }

    /**
     * Detects Firebase "permission-denied" errors in all their forms:
     *   - Direct FirebaseError from Firestore SDK.
     *   - Errors wrapped inside an RxJS ErrorEvent or zone.js re-throw.
     *   - Errors from async/await rejections bubbled through Zone.js.
     */
    private _isPermissionDenied(error: any): boolean {
        if (!error) return false;

        // Direct Firebase error code.
        if (error.code === 'permission-denied') return true;

        // Firestore SDK sometimes embeds the code in a nested cause.
        if (error.cause?.code === 'permission-denied') return true;

        // Fallback string check for messages from older SDK versions.
        const message: string = error.message ?? '';
        if (
            message.includes('permission-denied') ||
            message.includes('Missing or insufficient permissions')
        ) {
            return true;
        }

        return false;
    }
}
